import { useState } from "react";
import { motion, AnimatePresence } from "motion/react";
import {
  Users,
  ChevronDown,
  UserCircle2,
  ListChecks,
  Target,
  Search,
  Star,
} from "lucide-react";
import { Helmet } from "react-helmet";
import HeaderMenu from "../../Navbar/headermenu";
import CustomFooter from "../../Footer/customFooter";
import WorkTogether from "./WorkTogether";
import BeforeAfterSimplifyte from "./BeforeAfterSimplifyte";
import Onboard from "../../../OnBoard/Onboard";

/* ─── Static data ────────────────────────────────────────────────── */
const teamLeaders = [
  { id: 1, name: "Priya Sharma",    role: "Audit Lead",        team: "Audit & Assurance",    initials: "PS", color: "#7c3aed" },
  { id: 2, name: "Amit Mehta",      role: "Tax Lead",          team: "Direct Tax",            initials: "AM", color: "#0284c7" },
  { id: 3, name: "Riya Patel",      role: "GST Head",          team: "Indirect Tax",          initials: "RP", color: "#d97706" },
  { id: 4, name: "Siddharth Joshi", role: "Compliance Lead",   team: "Compliance & Filing",   initials: "SJ", color: "#16a34a" },
];

const teamMembersMap = {
  1: [
    { name: "Priya Sharma",   initials: "PS", color: "#7c3aed", role: "Audit Lead",      tasks: 6 },
    { name: "Karan Nair",     initials: "KN", color: "#0891b2", role: "Audit Senior",    tasks: 4 },
    { name: "Deepa Rao",      initials: "DR", color: "#db2777", role: "Audit Associate", tasks: 3 },
    { name: "Tejas Bhatt",    initials: "TB", color: "#ca8a04", role: "Article",          tasks: 5 },
  ],
  2: [
    { name: "Amit Mehta",     initials: "AM", color: "#0284c7", role: "Tax Lead",        tasks: 8 },
    { name: "Sneha Kulkarni", initials: "SK", color: "#16a34a", role: "Tax Senior",      tasks: 5 },
    { name: "Rohit Jain",     initials: "RJ", color: "#dc2626", role: "Tax Associate",   tasks: 3 },
  ],
  3: [
    { name: "Riya Patel",     initials: "RP", color: "#d97706", role: "GST Head",        tasks: 7 },
    { name: "Vikram Shah",    initials: "VS", color: "#7c3aed", role: "GST Senior",      tasks: 4 },
    { name: "Ankita Desai",   initials: "AD", color: "#0891b2", role: "GST Associate",   tasks: 6 },
  ],
  4: [
    { name: "Siddharth Joshi", initials: "SJ", color: "#16a34a", role: "Compliance Lead",  tasks: 5 },
    { name: "Pooja Verma",     initials: "PV", color: "#db2777", role: "Filing Senior",     tasks: 4 },
    { name: "Arjun Kapoor",    initials: "AK", color: "#0284c7", role: "Filing Associate",  tasks: 3 },
  ],
};

const allTasksMap = {
  1: [
    { id: 1,  project: "Audit FY25",  title: "Statutory Audit - ABC Ltd",     fy: "FY 2024-25", client: "ABC Ltd",     period: "Annual",  assignee: "Priya Sharma",   status: "in-progress", priority: "high"   },
    { id: 2,  project: "Audit FY25",  title: "Tax Audit Report u/s 44AB",     fy: "FY 2024-25", client: "XYZ Corp",    period: "Annual",  assignee: "Karan Nair",     status: "todo",        priority: "high"   },
    { id: 3,  project: "Audit FY25",  title: "Internal Audit - Q3",           fy: "FY 2024-25", client: "Patel Ind.",  period: "Q3",      assignee: "Deepa Rao",      status: "completed",   priority: "medium" },
    { id: 4,  project: "Audit FY25",  title: "Prepare Audit Working Papers",  fy: "FY 2024-25", client: "ABC Ltd",     period: "Annual",  assignee: "Tejas Bhatt",    status: "todo",        priority: "low"    },
    { id: 5,  project: "Audit FY25",  title: "Verification of Fixed Assets",  fy: "FY 2024-25", client: "Mehta Grp",  period: "Annual",  assignee: "Karan Nair",     status: "in-progress", priority: "medium" },
  ],
  2: [
    { id: 1,  project: "ITR FY25",    title: "ITR-6 Filing - XYZ Corp",       fy: "FY 2024-25", client: "XYZ Corp",   period: "Annual",  assignee: "Amit Mehta",     status: "in-progress", priority: "high"   },
    { id: 2,  project: "ITR FY25",    title: "Advance Tax Calculation Q4",    fy: "FY 2024-25", client: "Patel Ind.", period: "Q4",      assignee: "Sneha Kulkarni", status: "todo",        priority: "high"   },
    { id: 3,  project: "TDS",         title: "TDS Return - Q3 Filing",        fy: "FY 2024-25", client: "Mehta Grp",  period: "Q3",      assignee: "Rohit Jain",     status: "completed",   priority: "medium" },
    { id: 4,  project: "ITR FY25",    title: "Capital Gains Computation",     fy: "FY 2024-25", client: "Sharma Ltd", period: "Annual",  assignee: "Sneha Kulkarni", status: "todo",        priority: "medium" },
  ],
  3: [
    { id: 1,  project: "GST",         title: "GSTR-1 Filing - May 2025",      fy: "FY 2025-26", client: "ABC Ltd",    period: "May",     assignee: "Riya Patel",     status: "completed",   priority: "high"   },
    { id: 2,  project: "GST",         title: "GSTR-3B Reconciliation",        fy: "FY 2025-26", client: "XYZ Corp",   period: "May",     assignee: "Vikram Shah",    status: "in-progress", priority: "high"   },
    { id: 3,  project: "GST",         title: "Annual GST Return - GSTR-9",    fy: "FY 2024-25", client: "Patel Ind.", period: "Annual",  assignee: "Ankita Desai",   status: "todo",        priority: "medium" },
    { id: 4,  project: "GST",         title: "GST Audit Report u/r 101",      fy: "FY 2024-25", client: "Mehta Grp",  period: "Annual",  assignee: "Riya Patel",     status: "todo",        priority: "medium" },
    { id: 5,  project: "GST",         title: "Input Tax Credit Verification", fy: "FY 2024-25", client: "Sharma Ltd", period: "Annual",  assignee: "Vikram Shah",    status: "in-progress", priority: "low"    },
  ],
  4: [
    { id: 1,  project: "ROC",         title: "Annual Filing - Form AOC-4",    fy: "FY 2024-25", client: "ABC Ltd",    period: "Annual",  assignee: "Siddharth Joshi", status: "todo",       priority: "high"   },
    { id: 2,  project: "ROC",         title: "DIR-3 KYC for Directors",       fy: "FY 2024-25", client: "XYZ Corp",   period: "Annual",  assignee: "Pooja Verma",    status: "in-progress", priority: "medium" },
    { id: 3,  project: "Compliance",  title: "MSME Form-I Return",            fy: "FY 2024-25", client: "Patel Ind.", period: "H2",      assignee: "Arjun Kapoor",   status: "completed",   priority: "low"    },
    { id: 4,  project: "ROC",         title: "MGT-7A Annual Return Filing",   fy: "FY 2024-25", client: "Mehta Grp",  period: "Annual",  assignee: "Pooja Verma",    status: "todo",        priority: "high"   },
  ],
};

const statusConfig = {
  "todo":        { label: "To Do",       bg: "bg-gray-100",  text: "text-gray-700",  dot: "#6b7280" },
  "in-progress": { label: "In Progress", bg: "bg-blue-50",   text: "text-blue-700",  dot: "#3b82f6" },
  "completed":   { label: "Completed",   bg: "bg-green-50",  text: "text-green-700", dot: "#16a34a" },
};

const projectColors = {
  "Audit FY25": "#7c3aed", "ITR FY25": "#0284c7", "TDS": "#16a34a",
  "GST": "#d97706", "ROC": "#db2777", "Compliance": "#0891b2",
};

const testimonials = [
  {
    name: "Rakesh Dholia",
    role: "Partner",
    company: "Dholia Parmar Consultants",
    avatar: "RD",
    color: "bg-cyan-500",
    text: "Simplifyte gave us one place to track every client's GST, ITR, and audit deadlines instead of juggling spreadsheets. Our whole team finally works off the same board.",
  },
  {
    name: "Priya Sharma",
    role: "Senior Associate",
    company: "Sharma & Associates",
    avatar: "PS",
    color: "bg-rose-500",
    text: "Every client's documents and deadlines are finally in one place. No more digging through email threads to find last year's filing.",
  },
  {
    name: "Rajesh Mehta",
    role: "Founder",
    company: "Mehta Tax Consultants",
    avatar: "RM",
    color: "bg-amber-500",
    text: "Switching between Table and Calendar view changed how our team plans the week. We see exactly what's due before it becomes urgent.",
  },
];

/* ─── Sub-components ─────────────────────────────────────────────── */

function Avatar({ initials, color, size = 32, rounded = "8px" }) {
  return (
    <span
      style={{ background: color, width: size, height: size, borderRadius: rounded, fontSize: size * 0.35, flexShrink: 0 }}
      className="inline-flex items-center justify-center text-white font-semibold"
    >
      {initials}
    </span>
  );
}

function StatusBadge({ status }) {
  const cfg = statusConfig[status];
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-medium ${cfg.bg} ${cfg.text}`}>
      <span style={{ width: 6, height: 6, borderRadius: "50%", background: cfg.dot, flexShrink: 0 }} />
      {cfg.label}
    </span>
  );
}

/* ─── Feature section wrapper ────────────────────────────────────── */
function FeatureSection({ badge, badgeColor, title, description, bullets, bulletColor, demo, reverse = false }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.7 }}
      className="mb-24 md:mb-36"
    >
      <div className={`grid md:grid-cols-2 gap-10 lg:gap-16 items-center ${reverse ? "md:[&>*:first-child]:order-2" : ""}`}>
        {/* Text side */}
        <div>
          <span
            className="inline-block text-xs font-semibold uppercase tracking-widest px-3 py-1.5 rounded-full mb-5"
            style={{ background: `${badgeColor}18`, color: badgeColor }}
          >
            {badge}
          </span>
          <h2 className="text-gray-900 font-bold mb-4" style={{ fontSize: "clamp(1.5rem,3.5vw,2.25rem)", lineHeight: 1.2 }}>
            {title}
          </h2>
          <p className="text-gray-500 mb-7 leading-relaxed" style={{ fontSize: "clamp(0.875rem,1.5vw,1rem)" }}>
            {description}
          </p>
          <ul className="space-y-4">
            {bullets.map((b, i) => (
              <li key={i} className="flex gap-3">
                <span
                  className="mt-1 w-5 h-5 rounded-full flex items-center justify-center shrink-0"
                  style={{ background: `${bulletColor}18` }}
                >
                  <span style={{ width: 7, height: 7, borderRadius: "50%", background: bulletColor }} />
                </span>
                <div>
                  <p className="font-semibold text-gray-800" style={{ fontSize: "clamp(0.8rem,1.3vw,0.95rem)" }}>{b.title}</p>
                  <p className="text-gray-500 leading-relaxed" style={{ fontSize: "clamp(0.78rem,1.2vw,0.9rem)" }}>{b.desc}</p>
                </div>
              </li>
            ))}
          </ul>
        </div>
        {/* Demo side */}
        <motion.div
          className="rounded-2xl shadow-xl border border-gray-100 bg-white overflow-hidden"
          whileHover={{ y: -4, boxShadow: "0 24px 48px rgba(0,0,0,0.10)" }}
          transition={{ type: "spring", stiffness: 280 }}
        >
          {demo}
        </motion.div>
      </div>
    </motion.div>
  );
}

/* ─── Main component ─────────────────────────────────────────────── */
export default function TeamCollaborationFeature() {
  const [selectedLeader, setSelectedLeader]     = useState(teamLeaders[0]);
  const [dropdownOpen, setDropdownOpen]          = useState(false);
  const [selectedMember, setSelectedMember]      = useState(null);
  const [selectedStatus, setSelectedStatus]      = useState(null);

  const members = teamMembersMap[selectedLeader.id];
  const allTasks = allTasksMap[selectedLeader.id];

  const filteredTasks = allTasks.filter(t => {
    if (selectedMember && t.assignee !== selectedMember) return false;
    if (selectedStatus && t.status !== selectedStatus) return false;
    return true;
  });

  const statusCounts = {
    todo:        allTasks.filter(t => t.status === "todo").length,
    "in-progress": allTasks.filter(t => t.status === "in-progress").length,
    completed:   allTasks.filter(t => t.status === "completed").length,
  };

  /* reset filters on team change */
  const switchLeader = (leader) => {
    setSelectedLeader(leader);
    setDropdownOpen(false);
    setSelectedMember(null);
    setSelectedStatus(null);
  };

  return (
    <>
      <HeaderMenu />
      <Helmet>
        <title>Team Collaboration | Simplifyte</title>
      </Helmet>

      {/* Hero */}
      <WorkTogether />

      {/* Before / After */}
      <BeforeAfterSimplifyte />

      {/* ── Main feature sections ── */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-16">

        {/* ── Feature 1: Switch Team Leaders ── */}
        <FeatureSection
          badge="Feature 01"
          badgeColor="#7c3aed"
          title="Switch Between Team Leaders Instantly"
          description="Navigate across teams with a single click. Each team lead's workspace, tasks, and members load instantly — no page reload, no confusion."
          bulletColor="#7c3aed"
          bullets={[
            { title: "Quick Team Switching", desc: "Click the dropdown to switch to any team leader's view in real time." },
            { title: "See Leader Details",   desc: "View name, role, and department at a glance before switching." },
            { title: "Organised by Department", desc: "Audit, Tax, GST, Compliance — find any team in seconds." },
          ]}
          demo={
            <div>
              {/* Mock browser chrome */}
              <div className="flex items-center gap-1.5 px-4 py-3 border-b border-gray-100 bg-gray-50">
                <span className="w-3 h-3 rounded-full bg-red-300" />
                <span className="w-3 h-3 rounded-full bg-yellow-300" />
                <span className="w-3 h-3 rounded-full bg-green-300" />
              </div>

              <div className="p-5">
                {/* Team header with dropdown */}
                <div className="relative mb-5">
                  <button
                    onClick={() => setDropdownOpen(v => !v)}
                    className="flex items-center gap-2.5 group"
                  >
                    <Avatar initials={selectedLeader.initials} color={selectedLeader.color} size={36} />
                    <span className="font-bold text-gray-900 text-base">
                      {selectedLeader.name}'s Team
                    </span>
                    <ChevronDown
                      size={16}
                      className="text-gray-400 transition-transform duration-200"
                      style={{ transform: dropdownOpen ? "rotate(180deg)" : "none" }}
                    />
                  </button>

                  <AnimatePresence>
                    {dropdownOpen && (
                      <motion.div
                        initial={{ opacity: 0, y: -8, scale: 0.97 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: -8, scale: 0.97 }}
                        transition={{ duration: 0.15 }}
                        className="absolute left-0 top-full mt-1.5 w-72 bg-white rounded-xl border border-gray-200 shadow-xl z-20 overflow-hidden"
                      >
                        <div className="p-2 border-b border-gray-100">
                          <div className="flex items-center gap-2 px-2 py-1.5 bg-gray-50 rounded-lg">
                            <Search size={13} className="text-gray-400" />
                            <span className="text-xs text-gray-400">Select an option</span>
                          </div>
                        </div>
                        {teamLeaders.map(leader => (
                          <button
                            key={leader.id}
                            onClick={() => switchLeader(leader)}
                            className={`w-full flex items-center gap-3 px-3 py-2.5 text-left hover:bg-gray-50 transition-colors ${selectedLeader.id === leader.id ? "bg-indigo-50" : ""}`}
                          >
                            <Avatar initials={leader.initials} color={leader.color} size={30} rounded="6px" />
                            <div>
                              <p className="text-sm font-medium text-gray-900">{leader.name}</p>
                              <p className="text-xs text-gray-500">{leader.team}</p>
                            </div>
                          </button>
                        ))}
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>

                {/* Stats bar */}
                <div className="flex items-center gap-4 text-sm mb-4 pb-4 border-b border-gray-100">
                  {[
                    { label: "To Do",       count: statusCounts.todo,          color: "#6b7280" },
                    { label: "In Progress", count: statusCounts["in-progress"], color: "#3b82f6" },
                    { label: "Completed",   count: statusCounts.completed,      color: "#16a34a" },
                    { label: "Total",       count: allTasks.length,             color: "#093A5B" },
                  ].map(s => (
                    <div key={s.label} className="text-center">
                      <p className="font-bold" style={{ color: s.color, fontSize: "1.1rem" }}>{s.count}</p>
                      <p className="text-gray-400 text-xs">{s.label}</p>
                    </div>
                  ))}
                </div>

                {/* Mini task preview */}
                <div className="space-y-2">
                  {allTasks.slice(0, 3).map(t => (
                    <div key={t.id} className="flex items-center justify-between py-2 px-3 rounded-lg bg-gray-50">
                      <div className="flex items-center gap-2 min-w-0">
                        <span className="text-xs font-semibold" style={{ color: projectColors[t.project] || "#7c3aed" }}>
                          {t.project}
                        </span>
                        <span className="text-xs text-gray-600 truncate">{t.title}</span>
                      </div>
                      <StatusBadge status={t.status} />
                    </div>
                  ))}
                </div>

                <p className="text-center text-xs text-gray-400 mt-4">
                  👆 Click the team name above to switch
                </p>
              </div>
            </div>
          }
        />

        {/* ── Feature 2: Team Members ── */}
        <FeatureSection
          badge="Feature 02"
          badgeColor="#0284c7"
          title="View All Team Members at a Glance"
          description="See who's on the team, what they own, and how much they have on their plate — all without leaving your current view."
          bulletColor="#0284c7"
          reverse
          bullets={[
            { title: "Team Member Profiles",  desc: "Name, role and avatar displayed in a compact sidebar." },
            { title: "Task Count Display",    desc: "Instantly see how many tasks each member is currently handling." },
            { title: "Click to Filter",       desc: "Select any member to filter the task board to show only their work." },
            { title: "View All Option",       desc: 'Switch back to "All Members" to see the full team\'s tasks.' },
          ]}
          demo={
            <div className="p-5">
              <p className="text-xs text-gray-500 mb-1 font-medium">Team Members</p>
              <p className="text-xs text-gray-400 mb-4">Click any member to filter their tasks</p>

              {/* All members button */}
              <button
                onClick={() => setSelectedMember(null)}
                className={`w-full flex items-center justify-between px-4 py-3 rounded-xl mb-2 transition-all text-left ${
                  selectedMember === null
                    ? "text-white shadow-md"
                    : "bg-gray-50 border border-gray-200 text-gray-700 hover:border-blue-300"
                }`}
                style={selectedMember === null ? { background: "linear-gradient(135deg,#093A5B,#1e5080)" } : {}}
              >
                <div>
                  <p className="text-sm font-semibold">{selectedMember === null ? "✓ All Members" : "All Members"}</p>
                  <p className={`text-xs mt-0.5 ${selectedMember === null ? "text-blue-200" : "text-gray-400"}`}>
                    {members.length} members
                  </p>
                </div>
                <Users size={16} className={selectedMember === null ? "text-blue-200" : "text-gray-400"} />
              </button>

              {/* Individual members */}
              <div className="space-y-1.5">
                {members.map(m => (
                  <button
                    key={m.name}
                    onClick={() => setSelectedMember(m.name)}
                    className={`w-full flex items-center gap-3 px-4 py-2.5 rounded-xl transition-all text-left ${
                      selectedMember === m.name
                        ? "border-2 shadow-sm"
                        : "bg-gray-50 border border-gray-200 hover:border-gray-300"
                    }`}
                    style={selectedMember === m.name
                      ? { borderColor: m.color, background: `${m.color}08` }
                      : {}
                    }
                  >
                    <Avatar initials={m.initials} color={m.color} size={34} rounded="50%" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-gray-900 truncate">
                        {selectedMember === m.name ? "✓ " : ""}{m.name}
                      </p>
                      <p className="text-xs text-gray-500">{m.role}</p>
                    </div>
                    <span className="text-xs text-gray-400 shrink-0">{m.tasks} tasks</span>
                  </button>
                ))}
              </div>

              <p className="text-center text-xs text-gray-400 mt-4">
                👆 Try clicking different members
              </p>
            </div>
          }
        />

        {/* ── Feature 3: Filtered Task View ── */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="mb-24 md:mb-36"
        >
          <div className="text-center mb-10">
            <span className="inline-block text-xs font-semibold uppercase tracking-widest px-3 py-1.5 rounded-full mb-4"
              style={{ background: "#db277718", color: "#db2777" }}>
              Feature 03
            </span>
            <h2 className="font-bold text-gray-900 mb-3" style={{ fontSize: "clamp(1.5rem,3.5vw,2.25rem)" }}>
              Filtered Task View
            </h2>
            <p className="text-gray-500 max-w-xl mx-auto leading-relaxed" style={{ fontSize: "clamp(0.875rem,1.5vw,1rem)" }}>
              Filter by member or status and see exactly who's doing what — task name, project, FY, client and status in one clean table.
            </p>
          </div>

          <motion.div
            className="rounded-2xl border border-gray-100 shadow-xl bg-white overflow-hidden"
            whileHover={{ y: -4, boxShadow: "0 24px 48px rgba(0,0,0,0.10)" }}
            transition={{ type: "spring", stiffness: 280 }}
          >
            {/* Table toolbar */}
            <div className="flex flex-wrap items-center justify-between gap-3 px-5 py-4 border-b border-gray-100">
              <div className="flex items-center gap-2">
                <span className="text-sm font-semibold text-gray-800">All Tasks</span>
                <span className="text-xs px-2 py-0.5 bg-gray-100 text-gray-600 rounded-full">
                  {filteredTasks.length} shown
                </span>
              </div>

              {/* Status filter chips */}
              <div className="flex items-center gap-2 flex-wrap">
                {["todo", "in-progress", "completed"].map(s => {
                  const cfg = statusConfig[s];
                  const active = selectedStatus === s;
                  return (
                    <button
                      key={s}
                      onClick={() => setSelectedStatus(active ? null : s)}
                      className={`text-xs px-3 py-1 rounded-full border font-medium transition-all ${
                        active ? `${cfg.bg} ${cfg.text} border-transparent` : "bg-white text-gray-500 border-gray-200 hover:border-gray-400"
                      }`}
                    >
                      {active ? "✓ " : ""}{cfg.label}
                    </button>
                  );
                })}
                {(selectedMember || selectedStatus) && (
                  <button
                    onClick={() => { setSelectedMember(null); setSelectedStatus(null); }}
                    className="text-xs px-2.5 py-1 rounded-full bg-red-50 text-red-600 border border-red-100 font-medium hover:bg-red-100"
                  >
                    Clear ×
                  </button>
                )}
              </div>
            </div>

            {/* Table */}
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-100 bg-gray-50">
                    {["Project", "Task Name", "FY", "Client", "Period", "Assignee", "Status"].map(h => (
                      <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap">
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  <AnimatePresence mode="popLayout">
                    {filteredTasks.length === 0 ? (
                      <tr>
                        <td colSpan={7} className="px-4 py-8 text-center text-sm text-gray-400">
                          No tasks match the current filter
                        </td>
                      </tr>
                    ) : (
                      filteredTasks.map((t, i) => (
                        <motion.tr
                          key={`${selectedLeader.id}-${t.id}`}
                          initial={{ opacity: 0, x: -10 }}
                          animate={{ opacity: 1, x: 0 }}
                          exit={{ opacity: 0, x: 10 }}
                          transition={{ delay: i * 0.04 }}
                          className="border-b border-gray-50 hover:bg-gray-50 transition-colors"
                        >
                          <td className="px-4 py-3 whitespace-nowrap">
                            <span className="text-xs font-semibold" style={{ color: projectColors[t.project] || "#7c3aed" }}>
                              {t.project}
                            </span>
                          </td>
                          <td className="px-4 py-3 max-w-[200px]">
                            <span className="text-gray-800 text-xs">{t.title}</span>
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap">
                            <span className="text-xs font-medium text-blue-600">{t.fy}</span>
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap">
                            <span className="text-xs text-gray-600">{t.client}</span>
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap">
                            <span className="text-xs text-blue-500">{t.period}</span>
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap">
                            {/* find member color */}
                            {(() => {
                              const m = members.find(mb => mb.name === t.assignee);
                              return m ? (
                                <div className="flex items-center gap-1.5">
                                  <Avatar initials={m.initials} color={m.color} size={20} rounded="50%" />
                                  <span className="text-xs font-medium" style={{ color: m.color }}>{m.name.split(" ")[0]}</span>
                                </div>
                              ) : (
                                <span className="text-xs text-gray-500">{t.assignee}</span>
                              );
                            })()}
                          </td>
                          <td className="px-4 py-3">
                            <StatusBadge status={t.status} />
                          </td>
                        </motion.tr>
                      ))
                    )}
                  </AnimatePresence>
                </tbody>
              </table>
            </div>

            <div className="px-5 py-3 border-t border-gray-100 bg-gray-50 text-xs text-gray-400">
              Showing {filteredTasks.length} – {allTasks.length} of {allTasks.length} results &nbsp;·&nbsp;
              <span className="text-indigo-500 cursor-pointer hover:underline" onClick={() => { setSelectedMember(null); setSelectedStatus(null); }}>
                View all
              </span>
            </div>
          </motion.div>
        </motion.div>

        {/* ── What You Can Do ── */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="mb-24 md:mb-36 relative rounded-3xl overflow-hidden"
          style={{ background: "linear-gradient(135deg, #093A5B 0%, #0d4f7c 60%, #093A5B 100%)" }}
        >
          <div className="px-8 py-14 md:py-16 relative z-10">
            <div className="text-center mb-12">
              <h2 className="font-bold text-white mb-3" style={{ fontSize: "clamp(1.5rem,3.5vw,2.25rem)" }}>
                What You Can Do with Team Collaboration
              </h2>
              <p className="text-blue-200 max-w-lg mx-auto" style={{ fontSize: "clamp(0.875rem,1.5vw,1rem)" }}>
                Complete visibility and control over every team's workflow — all from one screen.
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-6">
              {[
                {
                  icon: UserCircle2,
                  title: "Manage Multiple Teams",
                  desc: "Switch between Audit, Tax, GST, and Compliance teams without losing context.",
                },
                {
                  icon: ListChecks,
                  title: "Track Individual Progress",
                  desc: "See each member's task load, deadlines and completion rate at a glance.",
                },
                {
                  icon: Target,
                  title: "Balance Workloads",
                  desc: "Spot overloaded members instantly and reassign tasks before deadlines slip.",
                },
              ].map((item, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.15 }}
                  whileHover={{ y: -6, backgroundColor: "rgba(255,255,255,0.13)" }}
                  className="rounded-2xl p-6 border border-white/10"
                  style={{ background: "rgba(255,255,255,0.07)" }}
                >
                  <div
                    className="w-11 h-11 rounded-xl flex items-center justify-center mb-4"
                    style={{ background: "rgba(255,255,255,0.12)" }}
                  >
                    <item.icon size={22} className="text-white" />
                  </div>
                  <h3 className="font-semibold text-white mb-2 text-base">{item.title}</h3>
                  <p className="text-blue-200 text-sm leading-relaxed">{item.desc}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </motion.div>

        {/* ── Testimonials ── */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="mb-24 md:mb-36"
        >
          <div className="text-center mb-10">
            <motion.div
              className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-yellow-100 to-amber-100 text-yellow-900 rounded-full mb-4"
              whileHover={{ scale: 1.05 }}
            >
              <Star className="w-4 h-4 fill-yellow-500 text-yellow-500" />
              Loved by Teams
            </motion.div>
            <h2 className="font-bold text-gray-900 mb-3" style={{ fontSize: "clamp(1.5rem,3.5vw,2.25rem)" }}>
              What Our Users Say
            </h2>
            <p className="text-gray-500 max-w-xl mx-auto leading-relaxed" style={{ fontSize: "clamp(0.875rem,1.5vw,1rem)" }}>
              Real feedback from professional teams running their practice on Simplifyte
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {testimonials.map((t, i) => (
              <motion.div
                key={i}
                className="bg-white rounded-2xl p-6 shadow-md border border-gray-100"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.15 }}
                whileHover={{ y: -6, boxShadow: "0 20px 40px rgba(0,0,0,0.10)" }}
              >
                <div className="flex gap-1 mb-4">
                  {[...Array(5)].map((_, j) => (
                    <Star key={j} className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                  ))}
                </div>
                <p className="text-gray-700 mb-6 italic testimonial_text leading-relaxed">
                  "{t.text}"
                </p>
                <div className="flex items-center gap-3">
                  <div className={`testimonial_icon ${t.color} rounded-full flex items-center justify-center text-white shadow-md font-semibold text-sm`}>
                    {t.avatar}
                  </div>
                  <div>
                    <p className="text-gray-900 font-semibold testimonial_name">{t.name}</p>
                    <p className="text-gray-500 text-xs testimonial_role">{t.role}</p>
                    <p className="text-gray-400 text-xs testimonial_cmp">{t.company}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>

      </section>

      {/* CTA / Onboard */}
      <Onboard />
      <CustomFooter />
    </>
  );
}
